/* eslint-disable max-classes-per-file -- HTTP errors and presenter contract failures take distinct fallback paths. */
import { z } from 'zod';

import type { SingleRootRouteProps } from './types';

export const ROUTE_PROPS_PATH = '/api/v4/single_root/route_props';

/** The Rails action's own path parameters (`id`, `job_id`), as its controller reads them. */
export type RailsRouteParams = Record<string, string>;

// Keyed by the page and its record ids, so each record's props are cached on their own.
export const routePropsQueryKey = (railsAction: string, railsParams: RailsRouteParams) =>
  ['singleRoot', 'routeProps', railsAction, railsParams] as const;

// `status` is what the shell client's `errorStatus` reads (a 401 reloads the page).
export class RoutePropsError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'RoutePropsError';
    this.status = status;
  }
}

interface RoutePropsSchema<TProps> {
  parse: (value: unknown) => TProps;
}

type RoutePropsParseFn<TProps> = (value: unknown) => TProps;

export type RoutePropsParser<TProps> = RoutePropsSchema<TProps> | RoutePropsParseFn<TProps>;

const envelopeSchema = z.looseObject({
  railsAction: z.string(),
  pageTitle: z.string(),
  flash: z.array(z.looseObject({ type: z.string(), text: z.string() })),
  props: z.record(z.string(), z.unknown())
});

export class RoutePropsContractError extends Error {
  constructor(railsAction: string) {
    super(`Route props contract for ${railsAction} is invalid`);
    this.name = 'RoutePropsContractError';
  }
}

const parseEnvelope = (railsAction: string, body: unknown): SingleRootRouteProps => {
  const result = envelopeSchema.safeParse(body);
  if (!result.success || result.data.railsAction !== railsAction) {
    throw new RoutePropsContractError(railsAction);
  }
  return result.data;
};

export const parseRouteProps = <TProps>(
  railsAction: string,
  body: unknown,
  parse: RoutePropsParser<TProps>
): SingleRootRouteProps<TProps> => {
  const envelope = parseEnvelope(railsAction, body);
  try {
    const props = typeof parse === 'function' ? parse(envelope.props) : parse.parse(envelope.props);
    return { ...envelope, props };
  } catch {
    throw new RoutePropsContractError(railsAction);
  }
};

export const fetchRouteProps = async (
  railsAction: string,
  railsParams: RailsRouteParams,
  fetchImpl: typeof fetch = async (...args) => fetch(...args)
): Promise<SingleRootRouteProps> => {
  const query = new URLSearchParams({ ...railsParams, railsAction });
  const response = await fetchImpl(`${ROUTE_PROPS_PATH}?${query.toString()}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' }
  });
  const body: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = z.object({ error: z.string() }).safeParse(body);
    throw new RoutePropsError(
      response.status,
      message.success ? message.data.error : response.statusText
    );
  }
  return parseEnvelope(railsAction, body);
};
